import { useNavigate, useParams } from "react-router-dom";
import PageTitle from "components/PageTitle";
import Page from "components/Page";
import useFetch from "hooks/use-fetch";
import { IVendResultResponse } from "./Receipt/models";
import {
    currencyLabel,
    hasUnits,
    prettyDate,
    totalCollectionFees,
    totalCollectionsExcl,
    totalFeesIncl,
    unit,
} from "../helpers";
import Button from "../components/Button";
import { ReceiptLine, ReceiptSection, ReceiptSummary, ReceiptTokenList } from "./Receipt/ReceiptParts";

export default function ReceiptPage() {
    const { vendId } = useParams();
    const { data, loading, error } = useFetch<IVendResultResponse>(`/api/v1/vends/${vendId}`);
    const nav = useNavigate();

    if (!vendId) {
        return <div className="p-4 text-center text-base">No vend specified</div>;
    }

    return (
        <Page align="left" compact>
            <PageTitle size="2xl">PPEX Receipt</PageTitle>
            {loading && <div className="text-base text-gray-600">Loading receipt...</div>}
            {!loading && error && (
                <>
                    <div className="text-base">No receipt found</div>
                    <Button className="w-full mt-4" onClick={() => nav("/")}>Done</Button>
                </>
            )}
            {!loading && data && (() => {
                const collections = data.data.collections || [];
                const collectionsTotal = totalCollectionsExcl(collections);
                const productTotal = data.data.productInfo.cost + data.data.productInfo.tax;
                const feesTotal = totalFeesIncl(
                    data.data.productInfo.feeCost,
                    data.data.productInfo.feeTax,
                    collections
                );

                const summaryLines = [];
                if (collectionsTotal > 0) {
                    summaryLines.push({ label: "Collections", value: currencyLabel(collectionsTotal) });
                }
                summaryLines.push({ label: "Product", value: currencyLabel(productTotal) });
                summaryLines.push({ label: "Fees", value: currencyLabel(feesTotal) });

                return (
                    <div className="space-y-4 text-base">
                        <div className="rounded-lg border border-gray-200 px-4 py-3">
                            <ReceiptLine label="Meter" value={data.data.meter.meterNumber} />
                            <ReceiptLine label="Vended on" value={prettyDate(data.data.vendDate)} />
                        </div>

                        <ReceiptSummary
                            lines={summaryLines}
                            total={currencyLabel(data.data.totalTendered)}
                        />

                        <ReceiptTokenList
                            tokens={data.data.tokens.map((token) => ({
                                type: token.type,
                                token: token.token,
                                units: hasUnits(token.units) ? token.units : undefined,
                                unit: unit(token.unit || data.data.productInfo.unitAbbr),
                            }))}
                        />

                        <h2 className="pt-1 text-sm font-bold uppercase tracking-wide text-gray-500">Detail</h2>

                        {collections.length > 0 && (
                            <ReceiptSection title="Collections" subdued>
                                <div className="py-1">
                                    {collections.map((collection, index) => (
                                        <ReceiptLine
                                            key={index}
                                            label={collection.reason}
                                            value={currencyLabel(collection.amountPaid)}
                                        />
                                    ))}
                                    <div className="mt-1 border-t border-gray-200 pt-2">
                                        <ReceiptLine
                                            label="Subtotal"
                                            value={currencyLabel(collectionsTotal)}
                                            bold
                                        />
                                    </div>
                                </div>
                            </ReceiptSection>
                        )}

                        <ReceiptSection title="Product" subdued>
                            <div className="py-1">
                                <ReceiptLine
                                    label="Cost (excl)"
                                    value={currencyLabel(data.data.productInfo.cost)}
                                />
                                <ReceiptLine
                                    label="Tax"
                                    value={currencyLabel(data.data.productInfo.tax)}
                                />
                                <div className="mt-1 border-t border-gray-200 pt-2">
                                    <ReceiptLine
                                        label="Subtotal"
                                        value={currencyLabel(productTotal)}
                                        bold
                                    />
                                </div>
                            </div>
                        </ReceiptSection>

                        <ReceiptSection title="Fees (incl)" subdued>
                            <div className="py-1">
                                {collections.length > 0 && (
                                    <ReceiptLine
                                        label="Collection fees"
                                        value={currencyLabel(totalCollectionFees(collections))}
                                    />
                                )}
                                <ReceiptLine
                                    label="Product fees"
                                    value={currencyLabel(data.data.productInfo.feeCost + data.data.productInfo.feeTax)}
                                />
                                <div className="mt-1 border-t border-gray-200 pt-2">
                                    <ReceiptLine
                                        label="Subtotal"
                                        value={currencyLabel(feesTotal)}
                                        bold
                                    />
                                </div>
                            </div>
                        </ReceiptSection>

                        <Button
                            className="w-full"
                            onClick={() => nav(`/meters/${data.data.meter.meterNumber}`)}
                        >
                            Done
                        </Button>
                    </div>
                );
            })()}
        </Page>
    );
}
